// Base
import { useState } from "react";

// Types
import type { FC, ChangeEvent } from "react";
import type { UseFormRegister, UseFormSetValue } from "react-hook-form";

// Store
import { useTemplateStore } from "@/zustand/templateStore";

// Assets
import { QuestionMark } from "@/assets/Icons";

interface CardCvcProps {
  placeholder: string;
  error?: string;
  name: string;
  value?: string;
  min?: number;
  max?: number;
  setValue: UseFormSetValue<any>;
  register: UseFormRegister<any>;
  handleChange?: (value: string) => void;
  className?: string;
  contrast?: boolean;
}

const CardCvc: FC<CardCvcProps> = ({
  placeholder,
  error,
  name,
  value,
  min,
  max,
  setValue,
  register,
  contrast,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { isMobile } = useTemplateStore();
  const { form } = useTemplateStore();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    setValue(name, numericValue, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return (
    <div className="relative w-full flex-1">
      <label
        className={`text-sm font-bold whitespace-nowrap ${
          contrast ? "text-cc-dark-gray" : "text-cc-gray"
        }`}
        htmlFor={name}
      >
        {placeholder}
      </label>

      <div className="relative">
        <input
          className={`w-full rounded-md border bg-white p-4 text-sm ${
            contrast
              ? "border-cc-gray-5 placeholder-cc-gray-6"
              : "border-cc-gray-4 placeholder-dark-gray"
          }`}
          maxLength={max}
          minLength={min}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={placeholder}
          autoComplete="cc-csc"
          value={value}
          {...register(name)}
          onChange={handleChange}
        />

        <div
          className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer"
          onClick={() => isMobile && setIsVisible(!isVisible)}
          onMouseEnter={() => !isMobile && setIsVisible(true)}
          onMouseLeave={() => !isMobile && setIsVisible(false)}
        >
          <QuestionMark className="text-cc-gray-3" />

          <div
            className={`absolute right-[-17px] bottom-[30px] w-[172px] scale-90 transform rounded bg-black/90 text-white transition-all duration-150 ${
              isVisible ? "z-30 scale-100 opacity-100" : "z-[-1] opacity-0"
            }`}
          >
            <div className="flex flex-wrap justify-between px-2 py-2 text-xs">
              {form?.tr_cvc_tooltip_message}
            </div>
            <div className="absolute right-7 bottom-[-3px] -rotate-45 border-[10px_0_0_10px] border-solid border-t-transparent border-r-transparent border-b-transparent border-l-black" />
          </div>
        </div>
      </div>

      {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
    </div>
  );
};

export default CardCvc;
